"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Graph = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const amm_1 = require("@zenlink-interface/amm");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const util_1 = require("../util");
const Edge_1 = require("./Edge");
const pools_1 = require("./pools");
const Vertice_1 = require("./Vertice");
const ROUTER_DISTRIBUTION_PORTION = 65535;
function getPoolType(pool) {
    switch (pool.constructor) {
        case pools_1.StandardPool:
        case pools_1.SyncPool:
        case pools_1.SolidlyPool:
            return amm_1.PoolType.Standard;
        case pools_1.StablePool:
        case pools_1.MetaPool:
            return amm_1.PoolType.Stable;
        case pools_1.UniV3Pool:
        case pools_1.GmxPool:
        case pools_1.JoeV2Pool:
        case pools_1.IZiPool:
        case pools_1.DodoV2Pool:
            return amm_1.PoolType.Concentrated;
        default:
            return amm_1.PoolType.Unknown;
    }
}
class Graph {
    constructor(pools, start, baseTokenOrNetworks, gasPriceSingleNetwork, minPriceLiquidity = 0) {
        const networks = Array.isArray(baseTokenOrNetworks)
            ? baseTokenOrNetworks
            : [
                {
                    chainId: baseTokenOrNetworks.chainId,
                    baseToken: baseTokenOrNetworks,
                    gasPrice: gasPriceSingleNetwork || 0,
                },
            ];
        (0, pools_1.setTokenId)(...networks.map(n => n.baseToken));
        this.vertices = [];
        this.edges = [];
        this.tokens = new Map();
        pools.forEach((p) => {
            const v0 = this.getOrCreateVertice(p.token0);
            const v1 = this.getOrCreateVertice(p.token1);
            const edge = new Edge_1.Edge(p, v0, v1);
            v0.edges.push(edge);
            v1.edges.push(edge);
            this.edges.push(edge);
        });
        const startV = this.getVert(start);
        if (startV !== undefined)
            this.setPricesStable(startV, 1, networks, minPriceLiquidity);
    }
    getOrCreateVertice(token) {
        let vert = this.getVert(token);
        if (vert)
            return vert;
        vert = new Vertice_1.Vertice(token);
        this.vertices.push(vert);
        this.tokens.set(token.tokenId, vert);
        return vert;
    }
    getVert(token) {
        return this.tokens.get(token.tokenId);
    }
    cleanCache() {
        this.edges.forEach(e => e.cleanCache());
        this.vertices.forEach(v => v.cleanCache());
    }
    addPath(from, to, path) {
        let _from = from;
        path.forEach((e) => {
            if (_from) {
                e.walk(_from);
                _from = _from.getNeibour(e);
            }
            else {
                console.error('Unexpected 315');
            }
        });
        (0, util_1.ASSERT)(() => {
            const res = this.vertices.every((v) => {
                let total = 0;
                let totalModule = 0;
                v.edges.forEach((e) => {
                    if (e.vert0 === v) {
                        if (e.direction)
                            total -= e.amountInPrevious;
                        else
                            total += e.amountInPrevious;
                        totalModule += e.amountInPrevious;
                    }
                    else {
                        if (e.direction)
                            total += e.amountOutPrevious;
                        else
                            total -= e.amountOutPrevious;
                        totalModule += e.amountOutPrevious;
                    }
                });
                if (v === from)
                    return total <= 0;
                if (v === to)
                    return total >= 0;
                if (totalModule === 0)
                    return total === 0;
                return Math.abs(total / totalModule) < 1e10;
            });
            return res;
        }, 'Error 290');
    }
    setPricesStable(from, price, networks, minLiquidity = 0) {
        const processedVert = new Set();
        let nextEdges = [];
        const edgeValues = new Map();
        const value = (e) => edgeValues.get(e);
        function addVertice(v, price) {
            v.price = price;
            const newEdges = v.edges.filter((e) => {
                if (processedVert.has(v.getNeibour(e)))
                    return false;
                if (e.pool.alwaysAppropriateForPricing())
                    return true;
                const liquidity = price * Number.parseInt(e.reserve(v).toString());
                if (liquidity < minLiquidity)
                    return false;
                edgeValues.set(e, liquidity);
                return true;
            });
            newEdges.sort((e1, e2) => value(e1) - value(e2));
            const res = [];
            while (nextEdges.length && newEdges.length) {
                if (value(nextEdges[0]) < value(newEdges[0]))
                    res.push(nextEdges.shift());
                else
                    res.push(newEdges.shift());
            }
            nextEdges = [...res, ...nextEdges, ...newEdges];
            processedVert.add(v);
        }
        addVertice(from, price);
        while (nextEdges.length > 0) {
            const bestEdge = nextEdges.pop();
            const [vFrom, vTo] = processedVert.has(bestEdge.vert1)
                ? [bestEdge.vert1, bestEdge.vert0]
                : [bestEdge.vert0, bestEdge.vert1];
            if (processedVert.has(vTo))
                continue;
            const p = bestEdge.pool.calcCurrentPriceWithoutFee(vFrom === bestEdge.vert1);
            addVertice(vTo, vFrom.price * p);
        }
        const gasPrice = new Map();
        networks.forEach((n) => {
            const vPrice = this.getVert(n.baseToken)?.price || 0;
            gasPrice.set(n.chainId, n.gasPrice * vPrice);
        });
        processedVert.forEach((v) => {
            const gasPriceChainId = gasPrice.get(v.token.chainId);
            (0, tiny_invariant_1.default)(gasPriceChainId !== undefined, 'SetPricesStable: GasPriceChainId');
            (0, tiny_invariant_1.default)(v.price !== 0, 'SetPricesStable: vertice price');
            v.gasPrice = gasPriceChainId / v.price;
        });
    }
    findBestPathExactIn(from, to, amountIn) {
        const start = this.getVert(from);
        const finish = this.getVert(to);
        if (!start || !finish)
            return;
        this.edges.forEach((e) => {
            e.bestEdgeIncome = 0;
            e.spentGasNew = 0;
        });
        this.vertices.forEach((v) => {
            v.bestIncome = 0;
            v.gasSpent = 0;
            v.bestTotal = 0;
            v.bestSource = undefined;
            v.checkLine = -1;
        });
        start.bestIncome = amountIn;
        start.bestTotal = amountIn;
        const processedVert = new Set();
        const nextVertList = [start];
        let debug_info = '';
        let checkLine = 0;
        for (;;) {
            let closestVert;
            let closestTotal;
            let closestPosition = 0;
            nextVertList.forEach((v, i) => {
                if (closestTotal === undefined || v.bestTotal > closestTotal) {
                    closestTotal = v.bestTotal;
                    closestVert = v;
                    closestPosition = i;
                }
            });
            if (!closestVert)
                return;
            closestVert.checkLine = checkLine++;
            if (closestVert === finish) {
                const bestPath = [];
                for (let v = finish; v?.bestSource; v = v.getNeibour(v.bestSource))
                    bestPath.unshift(v.bestSource);
                (0, util_1.DEBUG)(() => console.log(debug_info));
                return {
                    path: bestPath,
                    output: finish.bestIncome,
                    gasSpent: finish.gasSpent,
                    totalOutput: finish.bestTotal,
                };
            }
            nextVertList.splice(closestPosition, 1);
            closestVert.edges.forEach((e) => {
                const v2 = closestVert === e.vert0 ? e.vert1 : e.vert0;
                if (processedVert.has(v2))
                    return;
                let newIncome, gas;
                try {
                    const { output, gasSpent } = e.getOutput(closestVert, closestVert.bestIncome);
                    if (!Number.isFinite(output) || !Number.isFinite(gasSpent))
                        // Math errors protection
                        return;
                    newIncome = output;
                    gas = gasSpent;
                }
                catch (err) {
                    // Any arithmetic error or out-of-liquidity
                    e.bestEdgeIncome = -1;
                    return;
                }
                const newGasSpent = closestVert.gasSpent + gas;
                const price = v2.price / finish.price;
                const gasPrice = v2.gasPrice * price;
                const newTotal = newIncome * price - newGasSpent * gasPrice;
                (0, tiny_invariant_1.default)(e.bestEdgeIncome === 0, 'Error 373');
                e.bestEdgeIncome = newIncome * price;
                e.spentGasNew = e.spentGas + gas;
                if (!v2.bestSource)
                    nextVertList.push(v2);
                if (!v2.bestSource || newTotal > v2.bestTotal) {
                    (0, util_1.DEBUG)(() => {
                        const st = closestVert?.token === from ? '*' : '';
                        const fn = v2?.token === to ? '*' : '';
                        debug_info += `${st}${closestVert?.token.name}->${v2.token.name}${fn} ${v2.bestIncome} -> ${newIncome}\n`;
                    });
                    v2.bestIncome = newIncome;
                    v2.gasSpent = newGasSpent;
                    v2.bestTotal = newTotal;
                    v2.bestSource = e;
                }
            });
            processedVert.add(closestVert);
        }
    }
    findBestRouteExactIn(from, to, amountIn, mode) {
        let amountInBN;
        if (amountIn instanceof bignumber_1.BigNumber) {
            amountInBN = amountIn;
            amountIn = Number.parseInt(amountIn.toString());
        }
        else {
            amountInBN = (0, util_1.getBigNumber)(amountIn);
        }
        let routeValues = [];
        if (Array.isArray(mode)) {
            const sum = mode.reduce((a, b) => a + b, 0);
            routeValues = mode.map(e => e / sum);
        }
        else {
            for (let i = 0; i < mode; ++i)
                routeValues.push(1 / mode);
        }
        this.edges.forEach((e) => {
            e.amountInPrevious = 0;
            e.amountOutPrevious = 0;
            e.direction = true;
        });
        let output = 0;
        let gasSpentInit = 0;
        let totalOutput = 0;
        let totalrouted = 0;
        let primaryPrice;
        let step;
        for (step = 0; step < routeValues.length; ++step) {
            const p = this.findBestPathExactIn(from, to, amountIn * routeValues[step]);
            if (!p) {
                break;
            }
            else {
                output += p.output;
                gasSpentInit += p.gasSpent;
                totalOutput += p.totalOutput;
                this.addPath(this.getVert(from), this.getVert(to), p.path);
                totalrouted += routeValues[step];
            }
        }
        if (step === 0 || output === 0) {
            return {
                status: amm_1.RouteStatus.NoWay,
                fromToken: from,
                toToken: to,
                amountIn: 0,
                amountInBN: bignumber_1.BigNumber.from(0),
                amountOut: 0,
                amountOutBN: bignumber_1.BigNumber.from(0),
                legs: [],
                gasSpent: 0,
                totalAmountOut: 0,
                totalAmountOutBN: bignumber_1.BigNumber.from(0),
            };
        }
        let status;
        if (step < routeValues.length)
            status = amm_1.RouteStatus.Partial;
        else
            status = amm_1.RouteStatus.Success;
        this.removeEdgesWithLowFlow(0.001);
        const fromVert = this.getVert(from);
        const toVert = this.getVert(to);
        const { legs, gasSpent } = this.getRouteLegs(fromVert, toVert);
        (0, tiny_invariant_1.default)(gasSpent <= gasSpentInit, 'Internal Error 491');
        output = this.getLegsAmountOut(legs, amountIn * totalrouted);
        totalOutput = output - toVert.gasPrice * gasSpent;
        if (output === 0) {
            status = amm_1.RouteStatus.NoWay;
            totalOutput = 0;
        }
        let swapPrice, priceImpact;
        try {
            swapPrice = output / amountIn / totalrouted;
            const priceTo = this.getVert(to)?.price;
            const priceFrom = this.getVert(from)?.price;
            primaryPrice = priceTo && priceFrom ? priceFrom / priceTo : undefined;
            priceImpact = primaryPrice !== undefined ? 1 - swapPrice / primaryPrice : undefined;
        }
        catch (e) {
            /* skip division by 0 errors */
        }
        return {
            status,
            fromToken: from,
            toToken: to,
            primaryPrice,
            swapPrice,
            priceImpact,
            amountIn: amountIn * totalrouted,
            amountInBN: status === amm_1.RouteStatus.Success ? amountInBN : (0, util_1.getBigNumber)(amountIn * totalrouted),
            amountOut: output,
            amountOutBN: (0, util_1.getBigNumber)(output),
            legs,
            gasSpent,
            totalAmountOut: totalOutput, // TODO: should be recalculated if topologyWasChanged
            totalAmountOutBN: (0, util_1.getBigNumber)(totalOutput),
        };
    }
    getRouteLegs(from, to) {
        const { vertices, topologyWasChanged } = this.cleanTopology(from, to);
        const legs = [];
        let gasSpent = 0;
        vertices.forEach((n) => {
            const outEdges = n.getOutputEdges().map((e) => {
                const from = this.edgeFrom(e);
                return from ? [e, from.vert, from.amount] : [e];
            });
            let outAmount = outEdges.reduce((a, b) => a + b[2], 0);
            if (outAmount <= 0)
                return;
            const total = outAmount;
            // IMPORTANT: Casting to Number to avoid compiler bug which for some reason
            // keeps reference to outAmount which is mutated later
            // const total = Number(outAmount)
            // const totalTest = outAmount
            outEdges.forEach((e, i) => {
                const p = e[2];
                const quantity = i + 1 === outEdges.length ? 1 : p / outAmount;
                const edge = e[0];
                const poolType = getPoolType(edge.pool);
                legs.push({
                    poolId: edge.pool.poolId,
                    poolAddress: edge.pool.address,
                    poolType,
                    poolFee: edge.pool.fee,
                    tokenFrom: n.token,
                    tokenTo: n.getNeibour(edge).token,
                    assumedAmountIn: edge.direction ? edge.amountInPrevious : edge.amountOutPrevious,
                    assumedAmountOut: edge.direction ? edge.amountOutPrevious : edge.amountInPrevious,
                    swapPortion: quantity,
                    absolutePortion: p / total,
                });
                gasSpent += e[0].pool.swapGasCost;
                outAmount -= p;
            });
            (0, tiny_invariant_1.default)(outAmount / total < 1e-12, 'Error 281');
        });
        return { legs, gasSpent, topologyWasChanged };
    }
    getLegsAmountOut(legs, amountIn) {
        const amounts = new Map();
        amounts.set(legs[0].tokenFrom.tokenId, amountIn);
        legs.forEach((l) => {
            const vert = this.getVert(l.tokenFrom);
            (0, tiny_invariant_1.default)(vert !== undefined, 'Internal Error 570');
            const edge = vert.edges.find(e => e.pool.poolId === l.poolId);
            (0, tiny_invariant_1.default)(edge !== undefined, 'Internel Error 569');
            const pool = edge.pool;
            const direction = vert === edge.vert0;
            const inputTotal = amounts.get(l.tokenFrom.tokenId);
            (0, tiny_invariant_1.default)(inputTotal !== undefined, 'Internal Error 564');
            const routerPortion = Math.round(l.swapPortion * ROUTER_DISTRIBUTION_PORTION) / ROUTER_DISTRIBUTION_PORTION;
            const input = Math.floor(inputTotal * routerPortion);
            amounts.set(l.tokenFrom.tokenId, inputTotal - input);
            const output = pool.getOutput(input, direction).output;
            const vertNext = vert.getNeibour(edge);
            const prevAmount = amounts.get(vertNext.token.tokenId);
            amounts.set(vertNext.token.tokenId, (prevAmount || 0) + output);
            l.assumedAmountIn = input;
            l.assumedAmountOut = output;
        });
        return amounts.get(legs[legs.length - 1].tokenTo.tokenId) || 0;
    }
    edgeFrom(e) {
        if (e.amountInPrevious === 0)
            return undefined;
        return e.direction
            ? { vert: e.vert0, amount: e.amountInPrevious }
            : { vert: e.vert1, amount: e.amountOutPrevious };
    }
    removeEdgesWithLowFlow(minFraction) {
        const weakEdgeList = [];
        this.vertices.forEach((v) => {
            const outEdges = v.getOutputEdges();
            if (outEdges.length <= 1)
                return;
            const amounts = outEdges.map((e) => {
                const data = this.edgeFrom(e);
                if (data !== undefined)
                    return data.amount;
                console.error('Internal Error 1123');
                return 0;
            });
            const totalOut = amounts.reduce((a, b) => (a += b), 0);
            outEdges.forEach((e, i) => {
                if (amounts[i] / totalOut < minFraction)
                    weakEdgeList.push(e);
            });
        });
        weakEdgeList.forEach(e => (e.canBeUsed = false));
        return weakEdgeList.length;
    }
    cleanTopology(from, to) {
        let topologyWasChanged = false;
        let result = this.topologySort(from, to);
        if (result.status !== 2) {
            topologyWasChanged = true;
            (0, tiny_invariant_1.default)(result.status === 0, 'Internal Error 554');
            while (result.status === 0) {
                this.removeWeakestEdge(result.vertices);
                result = this.topologySort(from, to);
            }
            if (result.status === 3) {
                this.removeDeadEnds(result.vertices);
                result = this.topologySort(from, to);
            }
            (0, tiny_invariant_1.default)(result.status === 2, 'Internal Error 563');
            if (result.status !== 2)
                return { vertices: [], topologyWasChanged };
        }
        return { vertices: result.vertices, topologyWasChanged };
    }
    removeDeadEnds(verts) {
        verts.forEach((v) => {
            v.getInputEdges().forEach((e) => {
                e.canBeUsed = false;
            });
        });
    }
    removeWeakestEdge(verts) {
        let minVert;
        let minVertNext;
        let minOutput = Number.MAX_VALUE;
        verts.forEach((v1, i) => {
            const v2 = i === 0 ? verts[verts.length - 1] : verts[i - 1];
            let out = 0;
            v1.getOutputEdges().forEach((e) => {
                if (v1.getNeibour(e) !== v2)
                    return;
                out += e.direction ? e.amountOutPrevious : e.amountInPrevious;
            });
            if (out < minOutput) {
                minVert = v1;
                minVertNext = v2;
                minOutput = out;
            }
        });
        minVert?.getOutputEdges().forEach((e) => {
            if (minVert?.getNeibour(e) !== minVertNext)
                return;
            e.canBeUsed = false;
        });
    }
    topologySort(from, to) {
        // undefined or 0 - not processed, 1 - in process, 2 - finished, 3 - dedend
        const vertState = new Map();
        const vertsFinished = [];
        const foundCycle = [];
        const foundDeadEndVerts = [];
        // 0 - cycle was found and created, return
        // 1 - during cycle creating
        // 2 - vertex is processed ok
        // 3 - dead end vertex
        function topSortRecursive(current) {
            const state = vertState.get(current);
            if (state === 2 || state === 3)
                return state;
            if (state === 1) {
                (0, tiny_invariant_1.default)(foundCycle.length === 0, 'Internal Error 566');
                foundCycle.push(current);
                return 1;
            }
            vertState.set(current, 1);
            let successors2Exist = false;
            const outEdges = current.getOutputEdges();
            for (let i = 0; i < outEdges.length; ++i) {
                const e = outEdges[i];
                const res = topSortRecursive(current.getNeibour(e));
                if (res === 0)
                    return 0;
                if (res === 1) {
                    if (foundCycle[0] === current) {
                        return 0;
                    }
                    else {
                        foundCycle.push(current);
                        return 1;
                    }
                }
                if (res === 2)
                    successors2Exist = true; // Ok successors
            }
            if (successors2Exist) {
                (0, tiny_invariant_1.default)(current !== to, 'Internal Error 589');
                vertsFinished.push(current);
                vertState.set(current, 2);
                return 2;
            }
            else {
                if (current !== to) {
                    foundDeadEndVerts.push(current);
                    vertState.set(current, 3);
                    return 3;
                }
                vertsFinished.push(current);
                vertState.set(current, 2);
                return 2;
            }
        }
        const res = topSortRecursive(from);
        if (res === 0)
            return { status: 0, vertices: foundCycle };
        if (foundDeadEndVerts.length)
            return { status: 3, vertices: foundDeadEndVerts };
        (0, util_1.ASSERT)(() => {
            if (vertsFinished[0] !== to)
                return false;
            if (vertsFinished[vertsFinished.length - 1] !== from)
                return false;
            return true;
        }, 'Internal Error 614');
        if (res === 2)
            return { status: 2, vertices: vertsFinished.reverse() };
        (0, tiny_invariant_1.default)(true, 'Internal Error 612');
        return { status: 1, vertices: [] };
    }
}
exports.Graph = Graph;
